/**
 * QR Code Generator
 *
 * Generates QR codes as Fabric.js image objects on the canvas.
 * Uses the `qrcode` npm package for encoding, renders to an
 * offscreen canvas, then adds as a FabricImage.
 *
 * Styles:
 *   square  — standard sharp-edged QR modules
 *   rounded — modules with rounded corners
 *   dots    — circular dot modules
 */

import QRCode from 'qrcode';
import * as fabric from 'fabric';

/**
 * Generate a QR code and add it to the Fabric canvas.
 *
 * @param {fabric.Canvas} fabricCanvas - target canvas
 * @param {object} options
 * @param {string} options.url - URL or text to encode
 * @param {number} options.size - display size in pixels
 * @param {string} options.fgColor - foreground color
 * @param {string} options.bgColor - background color (use 'transparent' for none)
 * @param {string} options.style - 'square' | 'rounded' | 'dots'
 * @param {number} [options.x] - canvas x position
 * @param {number} [options.y] - canvas y position
 * @returns {Promise<fabric.FabricImage>} the added image object
 */
export async function addQrCode(fabricCanvas, options = {}) {
  const {
    url = 'https://stencil.cloud',
    size = 120,
    fgColor = '#000000',
    bgColor = '#ffffff',
    style = 'square',
    x = 40,
    y = 40,
  } = options;

  // Remove existing QR if present
  const existing = fabricCanvas.getObjects().find(o => o.data?.id === '__qr_code');
  if (existing) fabricCanvas.remove(existing);

  // Generate QR matrix data first to know the exact module count
  const qrData = QRCode.create(url, { errorCorrectionLevel: 'M' });
  const moduleCount = qrData.modules.size;
  const margin = 1; // modules of margin
  const totalModules = moduleCount + margin * 2;

  // Render at a high-res size that's an exact multiple of the module count
  // This ensures pixel-perfect module boundaries
  const modulePixels = Math.max(8, Math.ceil(300 / totalModules));
  const renderSize = totalModules * modulePixels;

  // For 'square' style, use the library's built-in renderer
  if (style === 'square') {
    const qrCanvas = document.createElement('canvas');
    await QRCode.toCanvas(qrCanvas, url, {
      width: renderSize,
      margin,
      color: {
        dark: fgColor,
        light: bgColor === 'transparent' ? '#00000000' : bgColor,
      },
      errorCorrectionLevel: 'M',
    });
    return addQrImage(fabricCanvas, qrCanvas, { x, y, size, renderSize, url, style, fgColor, bgColor });
  }

  // For styled QR codes, render manually using the matrix data
  const qrCanvas = document.createElement('canvas');
  qrCanvas.width = renderSize;
  qrCanvas.height = renderSize;
  const ctx = qrCanvas.getContext('2d');

  // Fill background
  if (bgColor !== 'transparent' && bgColor !== '#00000000') {
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, renderSize, renderSize);
  }

  ctx.fillStyle = fgColor;
  const modules = qrData.modules;

  for (let row = 0; row < moduleCount; row++) {
    for (let col = 0; col < moduleCount; col++) {
      if (!modules.get(row, col)) continue; // light module, skip

      const mx = (col + margin) * modulePixels;
      const my = (row + margin) * modulePixels;

      if (style === 'dots') {
        // Circles — slightly smaller than module to create gaps
        ctx.beginPath();
        ctx.arc(
          mx + modulePixels / 2,
          my + modulePixels / 2,
          modulePixels * 0.42,
          0,
          Math.PI * 2,
        );
        ctx.fill();
      } else if (style === 'rounded') {
        // Rounded rectangles
        const inset = modulePixels * 0.08;
        const r = modulePixels * 0.3;
        roundedRect(
          ctx,
          mx + inset,
          my + inset,
          modulePixels - inset * 2,
          modulePixels - inset * 2,
          r,
        );
        ctx.fill();
      }
    }
  }

  return addQrImage(fabricCanvas, qrCanvas, { x, y, size, renderSize, url, style, fgColor, bgColor });
}

/**
 * Helper: add a rendered QR canvas as a Fabric image.
 */
async function addQrImage(fabricCanvas, qrCanvas, opts) {
  const { x, y, size, renderSize, url, style, fgColor, bgColor } = opts;
  const dataUrl = qrCanvas.toDataURL('image/png');
  const img = await fabric.FabricImage.fromURL(dataUrl);
  const scale = size / renderSize;

  img.set({
    left: x,
    top: y,
    scaleX: scale,
    scaleY: scale,
    originX: 'left',
    originY: 'top',
    data: {
      id: '__qr_code',
      stencilType: 'qr',
      url, size, style, fgColor, bgColor,
    },
  });

  fabricCanvas.add(img);
  fabricCanvas.renderAll();
  return img;
}

/**
 * Remove QR code from canvas.
 */
export function removeQrCode(fabricCanvas) {
  const qr = fabricCanvas.getObjects().find(o => o.data?.id === '__qr_code');
  if (qr) {
    fabricCanvas.remove(qr);
    fabricCanvas.renderAll();
  }
}

function roundedRect(ctx, x, y, w, h, r) {
  r = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
